/**
 * sseEtherSubscriberApp -> ether5 card field mapping.
 *
 * Pure functions, exported separately from the template registration so they can
 * be exercised without a DOM. The entity has no display columns at all, so all
 * three come from the `tags` array:
 *
 *   caption  <- tags.app_caption   (page <title>, harvested on verification)
 *   title    <- tags.app_name      (supplied at create time)
 *   desc     <- tags.app_desc      (meta description, harvested on verification)
 *
 * Only app_name is required. The other two are filled by the domain ping in
 * POST /mine/{guid}/verify, and only when absent - see fillMissingTags there.
 * Every field falls back to the registered host so a card is never blank.
 */

/** Tag lookup. `tags` is [{tag, value}] and defaults to one empty entry. */
export function tagValue(app, tag) {
  const tags = Array.isArray(app?.tags) ? app.tags : [];
  return tags.find((t) => t?.tag === tag)?.value || '';
}

/**
 * The domain portion of a stored domain string.
 *
 * Hand-rolled on purpose. `app_domains[].domain` is documented as "Domains and
 * subdomains", so a scheme is the exception rather than the rule, and both
 * standard parsers fail on the common case:
 *
 *   new URL('acme.com')            throws
 *   URI.parse('acme.com').host     undefined - no scheme means no authority,
 *                                  the whole string is a path (RFC 3986)
 *   URI.parse('localhost:3333')    scheme 'localhost', path '3333'
 *
 * uri-js is a service-api dependency and is used in coreSnippet.js - which
 * pairs it with this same regex fallback for exactly these inputs. Adopting it
 * here would add a client bundle dependency and still need the fallback, while
 * not doing the lowercasing or www-stripping a caption wants.
 *
 * Strips scheme, credentials, path/query/hash, port, and a leading www.
 * Handles IPv6 literals: http://[::1]:8080/x -> [::1]
 */
export function domainOf(raw) {
  if (!raw) return '';

  return String(raw)
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')  // scheme
    .replace(/^[^@/]*@/, '')                  // user:pass@
    .split(/[/?#]/)[0]                        // path, query, fragment
    .replace(/:\d+$/, '')                     // port
    .replace(/^www\./i, '')                   // cosmetic
    .toLowerCase();
}

/** The registered domain, host only. The fallback identity for every display field. */
export function appHost(app) {
  const domains = Array.isArray(app?.app_domains) ? app.app_domains : [];
  return domainOf(domains[0]?.domain);
}

/**
 * Caption, title and description all come from `tags` - `app_caption`,
 * `app_name`, `app_desc`.
 *
 * Only `app_name` is required at create time. The other two are harvested from
 * the page on domain verification (<title> and the meta description), so a card
 * describes itself without the owner writing copy. Anything the owner does
 * supply is never overwritten by a later ping.
 *
 * Every fallback lands on the host rather than empty: an app whose domain never
 * verified has no harvested text, and a card with a blank band is
 * indistinguishable from a broken one.
 */
export function appCaption(app) {
  return tagValue(app, 'app_caption') || appHost(app) || 'no domain';
}

export function appTitle(app) {
  return tagValue(app, 'app_name') || appHost(app) || 'Untitled app';
}

/**
 * Newest timestamp in the `modified` audit trail, falling back to `created`.
 *
 * Takes the max rather than the last element: the array is append-ordered by
 * convention, but nothing enforces it, and one out-of-order write would
 * otherwise make an app look stale forever.
 */
export function lastTouchedISO(app) {
  const mods = Array.isArray(app?.modified) ? app.modified : [];

  const stamps = mods
    .map((m) => Date.parse(m?.modified_tz))
    .filter((n) => Number.isFinite(n));

  if (stamps.length) return new Date(Math.max(...stamps)).toISOString();
  return app?.created?.created_tz || '';
}

const RELATIVE_UNITS = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60]
];

/**
 * "Updated 3 days ago". Relative rather than absolute because the card exists to
 * support a selection decision at a glance, and recency is the signal - a date
 * has to be mentally differenced before it means anything.
 *
 * Intl.RelativeTimeFormat with numeric:'auto' yields "yesterday" over "1 day
 * ago" where the locale prefers it.
 */
export function humanWhen(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'Never updated';

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const deltaSec = (t - now) / 1000;
  const abs = Math.abs(deltaSec);

  for (const [unit, secs] of RELATIVE_UNITS) {
    if (abs >= secs) return `Updated ${rtf.format(Math.round(deltaSec / secs), unit)}`;
  }

  return 'Updated just now';
}

/**
 * The `app_desc` tag, falling back to recency, always carrying an unhealthy status.
 *
 * DRAFT is appended regardless of where the text came from. It is the default for
 * a newly created app and only clears once the domain answers 200, so it has to
 * survive the switch to harvested copy - otherwise the single most important
 * thing about a new app becomes the one thing the card does not say.
 */
export function appDesc(app, now = Date.now()) {
  const body = tagValue(app, 'app_desc') || humanWhen(lastTouchedISO(app), now);
  const status = app?.status;
  return status && status !== 'ENABLED' ? `${body} · ${status}` : body;
}

/**
 * Shape rhythm for the quilt.
 *
 * A placeholder rule: it repeats every 8 so a full set tiles without holes at
 * four columns, matching the pattern the mock used. When the entity carries real
 * signal worth emphasising - tier, status, traffic - drive it from that instead.
 */
const SHAPE_CYCLE = ['WIDE', 'SQUARE', 'SQUARE', 'TALL', 'SQUARE', 'WIDE', 'SQUARE', 'WIDE'];

export function appShape(index) {
  return SHAPE_CYCLE[index % SHAPE_CYCLE.length];
}

/**
 * Keep only apps belonging to the signed-in account.
 *
 * Belt and braces. GET /mine is owner-scoped server-side from the JWT, so this
 * should be a no-op - if it ever drops a row, that scoping has regressed. It is
 * NOT the security control; that lives in the route.
 *
 * With no resolvable owner guid, everything is returned rather than nothing, so
 * a broken lookup is visible instead of looking like an empty account.
 */
export function ownedBySignedIn(apps, ownerGuid) {
  if (!Array.isArray(apps)) return [];
  if (!ownerGuid) {
    console.warn('[etherApps] no signed-in guid - showing every app the API returned');
    return apps;
  }
  return apps.filter((a) => a?.owner_guid === ownerGuid);
}

export default {
  tagValue,
  domainOf,
  appHost,
  appCaption,
  appTitle,
  lastTouchedISO,
  humanWhen,
  appDesc,
  appShape,
  ownedBySignedIn
};
